import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ROUTE_MAIN_PATHS } from '@app/app.routes.paths';
import {
  ActionCollectionComponent,
  ActionCollectionItem,
} from '@app/components/action-collection';
import { BannerComponent } from '@app/components/banner';
import {
  ButtonComponent,
  ButtonIcon,
  ButtonSize,
  ButtonType,
  NativeButtonType,
} from '@app/components/button';
import { CardComponent } from '@app/components/card';
import { DialogComponent } from '@app/components/dialog';
import {
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextSpacing,
  TextType,
  TextWeight,
} from '@app/components/text';
import { ToastService } from '@app/components/toast';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import {
  AdminUserService,
  UserPermission,
} from '@app/services/admin-user/admin-user.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { environment } from '@environments/_current/environment';
import { TranslatePipe } from '@ngx-translate/core';
import { EnterpriseTenant, GympassTier } from '@skyfit/core-data-library';
import { AddUsersDialogComponent } from '../users/add-users/add-users.component';
import { InviteUsersComponent } from '../users/invite-users/invite-users.component';
import { PrimaryBannerComponent } from './components/primary-banner/primary-banner.component';
@Component({
  standalone: true,
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  imports: [
    CommonModule,
    BannerComponent,
    AddUsersDialogComponent,
    CardComponent,
    TextComponent,
    ActionCollectionComponent,
    DialogComponent,
    ButtonComponent,
    CommonModule,
    TranslatePipe,
    InviteUsersComponent,
    PrimaryBannerComponent,
  ],
})
export class HomeComponent implements OnInit {
  private readonly logger = inject(Logger);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly tenantService = inject(AdminTenantService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  userService = inject(AdminUserService);
  email = signal('');
  emailDialog = signal(false);
  inviteUserDialog = signal(false);
  companyLink = '';
  buttonText = 'Copy Message';
  inviteText = 'Send Invite';
  gymInfo = '';
  signUpLink = '';
  companyName = '';
  membershipTier = '';
  ButtonIcon = ButtonIcon;
  ButtonSize = ButtonSize;
  NativeButtonType = NativeButtonType;
  GympassTier = GympassTier;
  TextType = TextType;
  TextHeight = TextHeight;
  TextWeight = TextWeight;
  TextSize = TextSize;
  TextColor = TextColor;
  Spacing = Spacing;
  ButtonType = ButtonType;
  TextSpacing = TextSpacing;
  UserPermission = UserPermission;
  adminCollection: ActionCollectionItem[] = [
    {
      id: '1',
      title: 'Send Launch Email',
      subTitle:
        'Notify your team of their new benefit with a ready-to-send Aaptiv welcome email.',
      label: 'Email',
    },
    {
      id: '2',
      title: 'Invite Your Team',
      subTitle: 'Personalize Aaptiv for your company’s needs.',
      label: 'Invite Users',
    },
    {
      id: '3',
      title: 'Customize Your Experience',
      subTitle: 'Personalize Aaptiv for your company’s needs.',
      label: 'Customize',
    },
  ];

  readonly hasMetricChallenges = signal(false);

  ngOnInit() {
    this.tenantService.tenant$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenant) => {
        this.companyName = tenant.name;
        this.membershipTier =
          tenant.features?.gympass?.config?.availableTiers?.[0]?.subsidy ??
          GympassTier.Digital;
        this.hasMetricChallenges.set(
          this.tenantService.hasFeature('metricChallenges')
        );
        this.setLink(tenant);
        this.setGym(tenant);
      });
  }

  setLink(tenant: EnterpriseTenant) {
    const slug = tenant?.config?.slugs?.value?.[0];
    if (!slug) {
      this.logger.warn('Tenant has no slug value');
    }
    this.companyLink = `${environment.aaptivUserPortalUrl}/join/${slug}`;
    this.signUpLink = `${environment.aaptivUserPortalUrl}/join/${slug}`;
  }

  handleCopyLink() {
    const message = this.translatorService.translate('common.copiedClipboard');
    this.toastService.add(message);
    navigator.clipboard.writeText(this.companyLink);
  }

  setGym(tenant: EnterpriseTenant): void {
    const gymPass = tenant?.features?.gympass?.value;
    if (!gymPass) {
      return;
    }
    this.gymInfo = ` and a network of up to 20,000+ gyms and fitness studios `;
  }

  handleCopyEmail() {
    const message = this.translatorService.translate('home.emailTemplate', {
      gymInfo: this.gymInfo,
      companyName: this.companyName,
      membershipTier: this.membershipTier,
      signUpLink: this.signUpLink,
    });
    const toastMessage = this.translatorService.translate(
      'home.emailCopiedClipboard'
    );
    this.toastService.add(toastMessage);
    navigator.clipboard.writeText(message);
  }

  handleAction(item: ActionCollectionItem) {
    if (item.id === '1') {
      this.handleEmailDialog();
    } else if (item.id === '2') {
      this.handleInviteDialog();
    } else if (item.id === '3') {
      this.router.navigateByUrl(ROUTE_MAIN_PATHS.appConfig);
    }
  }

  handleEmailDialog() {
    this.emailDialog.set(!this.emailDialog());
  }

  handleInviteDialog() {
    this.inviteUserDialog.set(!this.inviteUserDialog());
  }
}
